import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, Facebook, Linkedin, Instagram, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import hexatechLogo from "@/assets/hexatech-logo.webp";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useSocialLinks } from "@/hooks/useSocialLinks";
import { isBR, BR_COMPANY, MARKET, pickMarketRows } from "@/lib/market";
import { usePublicContactSettings } from "@/hooks/usePublicContactSettings";

interface NavigationItem {
  id: string;
  label: string;
  url: string;
  section: string | null;
  opens_in_new_tab: boolean;
}

interface FooterSetting {
  setting_key: string;
  setting_value: string;
}

export function Footer() {
  const { identity } = usePublicContactSettings();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "pt").split("-")[0];
  const localizedLabel = (item: any) => {
    if (lang !== "pt") {
      const v = item[`label_${lang}`];
      if (v && String(v).trim() !== "") return v;
    }
    return item.label;
  };
  const localizedName = (item: any) => {
    if (lang !== "pt") {
      const v = item[`name_${lang}`];
      if (v && String(v).trim() !== "") return v;
    }
    return item.name;
  };
  const { data: socialFromAdmin } = useSocialLinks();

  // Fetch footer navigation items
  const { data: footerNavItems } = useQuery({
    queryKey: ["footer-navigation", MARKET],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("navigation_settings")
        .select("*")
        .eq("location", "footer")
        .eq("is_visible", true)
        .in("market", [MARKET, "pt"])
        .order("display_order");
      if (error) throw error;
      return pickMarketRows(data as any, MARKET) as NavigationItem[];
    },
  });

  // Fetch visible equipment categories
  const { data: equipmentCategories } = useQuery({
    queryKey: ["footer-equipment-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("equipment_categories")
        .select("name, slug, name_en, name_es, name_fr, name_pl")
        .eq("show_in_menu", true)
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  // Fetch footer settings
  const { data: footerSettings } = useQuery({
    queryKey: ["footer-settings-content", MARKET],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("footer_settings")
        .select("setting_key, setting_value, market")
        .in("market", [MARKET, "pt"]);
      if (error) throw error;
      const settingsMap: Record<string, string> = {};
      (pickMarketRows(data as any, MARKET) as FooterSetting[]).forEach((s) => {
        settingsMap[s.setting_key] = s.setting_value;
      });
      return settingsMap;
    },
  });

  const getSetting = (key: string, fallback: string = "") => {
    return footerSettings?.[key] || fallback;
  };

  const empresaLinks = footerNavItems?.filter(item => item.section === "empresa") || [];
  const suporteLinks = footerNavItems?.filter(item => item.section === "suporte") || [];
  const legalLinks = footerNavItems?.filter(item => item.section === "legal") || [];

  // Prefer links escolhidos em /admin/configuracoes (app_settings)
  const facebookUrl = socialFromAdmin?.facebook_url || getSetting("facebook_url");
  const linkedinUrl = socialFromAdmin?.linkedin_url || getSetting("linkedin_url");
  const instagramUrl = socialFromAdmin?.instagram_url || getSetting("instagram_url");

  return (
    <footer className="bg-foreground text-background">
      {/* CTA Section */}
      <div className="bg-primary py-12">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-2">
                {getSetting("cta_title", "Pronto para Transformar o Seu Negócio?")}
              </h3>
              <p className="text-primary-foreground">
                {getSetting("cta_subtitle", "Contacte-nos para uma demonstração ou orçamento personalizado")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" variant="secondary" asChild className="bg-background text-primary hover:bg-background/90">
                <Link to="/contacto">
                  Pedir Orçamento
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-primary-foreground bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Link to="/auth?mode=register">
                  Criar Conta Empresarial
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-5">
            <img 
              src={hexatechLogo} 
              alt="HEXATECH - Impressão Digital Profissional DTF" 
              className="h-10 brightness-0 invert"
              width={82}
              height={40}
              style={{ aspectRatio: '82/40', width: 'auto' }}
              loading="lazy"
              decoding="async"
            />
            <p className="text-sm text-background/70 leading-relaxed">
              {isBR ? "Consumíveis DTF de alta qualidade para empresas no Brasil. Vendas exclusivas para CNPJ." : getSetting("company_description", "Especialistas em máquinas de impressão digital e consumíveis para empresas.")}
            </p>
            <div className="flex gap-3" role="list" aria-label="Redes sociais">
              {facebookUrl && (
                <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center text-background/75 hover:bg-background/20 hover:text-background transition-colors" aria-label="Visitar página do Facebook">
                  <Facebook className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
              {linkedinUrl && (
                <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center text-background/75 hover:bg-background/20 hover:text-background transition-colors" aria-label="Visitar página do LinkedIn">
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
              {instagramUrl && (
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-background/10 flex items-center justify-center text-background/75 hover:bg-background/20 hover:text-background transition-colors" aria-label="Visitar página do Instagram">
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
            </div>
            {/* Language Selector */}
            {!isBR && <LanguageSelector variant="footer" showLabel />}
          </div>

          {/* Equipamentos (apenas Portugal — o Brasil vende consumíveis) */}
          {(
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-background/75 mb-4">
                Equipamentos
              </h3>
              <ul className="space-y-3">
                {equipmentCategories?.map(category => (
                  <li key={category.slug}>
                    <Link to={`/equipamentos/${category.slug}`} className="text-sm text-background/70 hover:text-background transition-colors inline-flex items-center group">
                      <span className="group-hover:translate-x-1 transition-transform">{localizedName(category)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Links Dinâmicos - Empresa/Suporte */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-background/75 mb-4">
              Empresa
            </h3>
            <ul className="space-y-3">
              {empresaLinks.filter(item => !(isBR && /assistencia|equipament|financiamento|blog/.test(item.url))).map(item => (
                <li key={item.id}>
                  {item.opens_in_new_tab ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sm text-background/70 hover:text-background transition-colors">
                      {localizedLabel(item)}
                    </a>
                  ) : (
                    <Link to={item.url} className="text-sm text-background/70 hover:text-background transition-colors">
                      {localizedLabel(item)}
                    </Link>
                  )}
                </li>
              ))}
              {suporteLinks.filter(item => !(isBR && /assistencia|equipament|financiamento|blog/.test(item.url))).map(item => (
                <li key={item.id}>
                  {item.opens_in_new_tab ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sm text-background/70 hover:text-background transition-colors">
                      {localizedLabel(item)}
                    </a>
                  ) : (
                    <Link to={item.url} className="text-sm text-background/70 hover:text-background transition-colors">
                      {localizedLabel(item)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-background/75 mb-4">
              Contacto
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-background/70">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5 text-primary" aria-hidden="true" />
                <span>
                  {isBR ? BR_COMPANY.addressLine1 : identity.addressLine1}<br />
                  {isBR ? BR_COMPANY.addressLine2 : identity.addressLine2}
                  {!isBR && !identity.addressLine2.includes(identity.country) && <>, {identity.country}</>}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${(isBR ? BR_COMPANY.phone : getSetting("phone_primary", "+351963920495")).replace(/\s/g, "")}`}
                  className="flex items-start gap-3 text-sm text-background/70 hover:text-background transition-colors"
                  aria-label={`Ligar para ${isBR ? BR_COMPANY.phone : getSetting("phone_primary", "+351 963 920 495")}${!isBR && getSetting("phone_secondary") ? ` ou ${getSetting("phone_secondary")}` : ""}`}
                >
                  <Phone className="h-5 w-5 flex-shrink-0 mt-0.5 text-primary" aria-hidden="true" />
                  <span>
                    {isBR ? BR_COMPANY.phone : getSetting("phone_primary", "+351 963 920 495")}
                    {!isBR && getSetting("phone_secondary") && (
                      <>
                        <br />
                        {getSetting("phone_secondary")}
                      </>
                    )}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${isBR ? BR_COMPANY.email : identity.email}`}
                  className="flex items-center gap-3 text-sm text-background/70 hover:text-background transition-colors"
                  aria-label={`Enviar email para ${isBR ? BR_COMPANY.email : identity.email}`}
                >
                  <Mail className="h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />
                  <span>{isBR ? BR_COMPANY.email : identity.email}</span>
                </a>
              </li>
              {isBR && (
                <li className="text-sm text-background/70">
                  <span className="font-medium text-background/85">{BR_COMPANY.name}</span><br />
                  CNPJ {BR_COMPANY.cnpj}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-background/10">
        <div className="container py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-background/75">
            © {new Date().getFullYear()} {isBR ? BR_COMPANY.copyright : getSetting("copyright_text", "HEXATECH - printing partner. Todos os direitos reservados.")}
          </p>
          <div className="flex gap-6">
            <Link to="/termos-condicoes" className="text-sm text-background/75 hover:text-background transition-colors">
              Termos e Condições
            </Link>
            <Link to="/politica-privacidade" className="text-sm text-background/75 hover:text-background transition-colors">
              Política de Privacidade
            </Link>
            {legalLinks
              .filter(item => !item.url.includes("termos") && !item.url.includes("privacidade") && !item.url.includes("privacy"))
              .map(item => (
                item.opens_in_new_tab ? (
                  <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="text-sm text-background/75 hover:text-background transition-colors">
                    {localizedLabel(item)}
                  </a>
                ) : (
                  <Link key={item.id} to={item.url} className="text-sm text-background/75 hover:text-background transition-colors">
                    {localizedLabel(item)}
                  </Link>
                )
              ))}
          </div>
        </div>
      </div>
    </footer>
  );
}